import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, LOGO_URL } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Mail, LineChart, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@newportstays.co.uk");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // 2FA challenge state
  const [challenge, setChallenge] = useState(null); // { challenge_id, email }
  const [code, setCode] = useState("");

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await login(email, password);
      if (result?.two_factor_required) {
        setChallenge({ challenge_id: result.challenge_id, email: result.email });
        toast.success("We've emailed you a 6-digit sign-in code");
      } else {
        toast.success("Welcome back to Newport Stays");
        nav("/");
      }
    } catch (err) {
      setError(apiError(err, "Invalid credentials"));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verify2fa(challenge.challenge_id, code.trim());
      toast.success("Welcome back to Newport Stays");
      nav("/");
    } catch (err) {
      setError(apiError(err, "Incorrect code"));
    } finally {
      setBusy(false);
    }
  };

  const cancelChallenge = () => {
    setChallenge(null);
    setCode("");
    setError("");
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotDone("");
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: forgotEmail,
      });
      setForgotDone(
        data.message ||
          "If that email is on Newport Stays, a temporary password has been sent.",
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-stone-50">
      <div className="ns-login-hero hidden lg:flex flex-col justify-between p-14 text-white">
        <div className="flex items-center gap-4">
          <img
            src={LOGO_URL}
            alt="Newport Stays"
            className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/30"
          />
          <div>
            <div className="font-serif text-2xl">HubManagr</div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-teal-200/90">
              The Ultimate Channel Manager & CRM
            </div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="ns-label mb-4 text-teal-200/80">Every guest, contractor and companion — kept close.</div>
          <h2 className="font-serif text-5xl leading-tight">
            Newport Stays - Channel Manager, CRM &amp; Ledger
          </h2>
          <p className="mt-6 text-white/80 leading-relaxed">
            One quiet place to remember every stay, mail-shot past guests after
            departure, view financial performance, record Compliance &amp; Utilities
            and manage Cleaning teams.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4" data-testid="login-highlights">
            {[
              [LineChart, "Finance", "Income, costs & profit per property"],
              [ShieldCheck, "Compliance & Utilities", "Certificates, leases & bills"],
              [Sparkles, "Cleaning teams", "Turnovers, photos & check-ins"],
            ].map(([Icon, title, sub]) => (
              <div key={title} className="group" data-testid={`login-highlight-${title.split(" ")[0].toLowerCase()}`}>
                <div className="w-10 h-10 rounded-full bg-white/10 border border-white/15 flex items-center justify-center mb-2 transition-transform duration-300 group-hover:-translate-y-0.5">
                  <Icon className="w-4 h-4 text-teal-200" />
                </div>
                <div className="text-sm text-white">{title}</div>
                <div className="text-[11px] text-white/60 leading-snug">{sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-white/40 tracking-widest uppercase">
          © {new Date().getFullYear()} Newport Stays
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {challenge ? (
          <form
            onSubmit={onVerify}
            className="w-full max-w-md bg-white border border-slate-200/80 rounded-2xl p-10 shadow-[0_20px_60px_rgba(10,17,40,0.06)]"
          >
            <div className="lg:hidden mb-8 flex items-center gap-3">
              <img src={LOGO_URL} className="w-10 h-10 rounded-lg" alt="" />
              <div className="font-serif text-xl">Newport Stays</div>
            </div>
            <div className="ns-label mb-2">Two-step sign-in</div>
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900">
              Check your email
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              We&apos;ve sent a 6-digit code to
              <span className="font-medium text-slate-700"> {challenge.email}</span>.
              It expires in 10 minutes.
            </p>
            <div className="mt-8 space-y-5">
              <div>
                <Label className="ns-label">Verification code</Label>
                <Input
                  data-testid={AUTH.twoFactorCode}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 h-14 bg-white text-center text-2xl tracking-[0.6em] font-mono"
                  placeholder="000000"
                  required
                  autoFocus
                />
              </div>
              {error && (
                <div
                  data-testid={AUTH.errorMsg}
                  className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3"
                >
                  {error}
                </div>
              )}
              <Button
                data-testid={AUTH.twoFactorSubmit}
                disabled={busy || code.length < 6}
                className="w-full h-11 rounded-full ns-btn-primary text-white text-sm tracking-wide"
                type="submit"
              >
                {busy ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button
                type="button"
                onClick={cancelChallenge}
                className="w-full text-xs text-slate-400 hover:text-slate-600 tracking-wider"
              >
                ← Back to sign in
              </button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md bg-white border border-slate-200/80 rounded-2xl p-10 shadow-[0_20px_60px_rgba(10,17,40,0.06)]"
          >
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src={LOGO_URL} className="w-10 h-10 rounded-lg" alt="" />
            <div className="font-serif text-xl">Newport Stays</div>
          </div>
          <div className="ns-label mb-2">Sign in</div>
          <h1 className="font-serif text-3xl md:text-4xl text-slate-900">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Log in to access Channel Manager, CRM and Email Campaigns.
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <Label className="ns-label">Email</Label>
              <Input
                data-testid={AUTH.emailInput}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 bg-white"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="ns-label">Password</Label>
                <button
                  type="button"
                  data-testid={AUTH.forgotLink}
                  onClick={openForgot}
                  className="text-[11px] tracking-wider uppercase text-teal-700 hover:underline"
                >
                  Forgot?
                </button>
              </div>
              <Input
                data-testid={AUTH.passwordInput}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 bg-white"
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div
                data-testid={AUTH.errorMsg}
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3"
              >
                {error}
              </div>
            )}

            <Button
              data-testid={AUTH.submitBtn}
              disabled={busy}
              className="w-full h-11 rounded-full ns-btn-primary text-white text-sm tracking-wide"
              type="submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <div className="mt-8 text-xs text-slate-400 text-center tracking-wider">
            Trouble signing in? Contact your Newport Stays owner.
          </div>
        </form>
        )}
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-teal-700" />
              Reset your password
            </DialogTitle>
          </DialogHeader>

          {forgotDone ? (
            <div className="py-2 space-y-4">
              <div className="flex items-start gap-3 text-sm bg-teal-50 border border-teal-100 rounded-xl p-4">
                <Mail className="w-5 h-5 text-teal-700 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-800">
                    Check your inbox
                  </div>
                  <div className="text-slate-600 mt-1">{forgotDone}</div>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Sign in with the temporary password we just emailed you, then
                head to Settings to choose a new one.
              </p>
              <DialogFooter>
                <Button
                  onClick={() => setForgotOpen(false)}
                  className="rounded-full ns-btn-primary px-6"
                >
                  Got it
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="py-2 space-y-4">
              <p className="text-sm text-slate-500">
                Enter the email you use to sign in. We&apos;ll email you a
                fresh temporary password.
              </p>
              <div>
                <Label className="ns-label">Email</Label>
                <Input
                  data-testid={AUTH.forgotEmailInput}
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="mt-2 h-11 bg-white"
                  required
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setForgotOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={AUTH.forgotSubmitBtn}
                  type="submit"
                  disabled={forgotBusy || !forgotEmail}
                  className="rounded-full ns-btn-primary px-6"
                >
                  {forgotBusy ? "Sending…" : "Email me a new password"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
